<template lang="pug">
    .establishment-editor
        v-page-loader(:show="isLoading")
        v-card.elevation-10(v-if="!isLoading" )
            v-card-title.editor-title.mb-3
                h2.headline {{ $vuetify.lang.t(`$vuetify.pages.Products.${isEdit ? 'edit' : 'create'}`)}}
                .d-flex
                  v-checkbox(
                    v-model="product.isByWeight"
                    :label="$vuetify.lang.t(`$vuetify.pages.Products.table.isByWeight`)"
                    color="secondary"
                  )
            v-card-text
                v-locale(controlled v-model="editingLocale")
                v-row.mt-3
                    v-col.py-0(cols="12" sm="4")
                        v-text-field(
                            v-model="product[localizeField('name', editingLocale)]"
                            :label="$vuetify.lang.t('$vuetify.pages.Products.table.name') + ' *'"
                            outlined
                            counter="255"
                            maxLength="255"
                        )
                    v-col.py-0(cols="12" sm="4")
                        v-row.ma-0
                            v-col.py-0(cols="12" sm="6")
                                v-text-field(
                                    v-model="product.weight"
                                    :label="$vuetify.lang.t('$vuetify.pages.Products.table.weight') + ' *'"
                                    :error-messages="getValidateMessage('Product', 'product.weight', 'weight')"
                                    type="number"
                                    min="0"
                                    outlined
                                )
                            v-col.py-0(cols="12" sm="6")
                                v-select(
                                  v-model="product.quantityType"
                                  :items="quantityTypeOptions"
                                  item-value="id"
                                  item-text="name"
                                  outlined
                                )
                    v-col.d-flex.align-start.py-0(cols="12" sm="4")
                        v-text-field(
                            v-model="product.price"
                            :label="$vuetify.lang.t('$vuetify.pages.Products.table.price') + ' *'"
                            :error-messages="getValidateMessage('Product', 'product.price', 'price')"
                            type="number"
                            min="0"
                            outlined
                        )
                        v-tooltip(right v-if="product.establishment")
                            template(v-slot:activator='{ on, attrs }')
                                v-icon.pl-1.pt-3(v-on="on") info
                            div {{$vuetify.lang.t('$vuetify.pages.Products.table.establishmentPrice')}}: {{ +(product.price * (1 - +product.establishment.commission)).toFixed(2) }}{{$vuetify.lang.t('$vuetify.hrn')}}
                            div {{$vuetify.lang.t('$vuetify.pages.Products.table.clientPrice')}}:&nbsp;
                                span(:class="{redText: product.establishment.margin !== '0' || product.section.margin !== '0'}" v-if="product.establishment && product.section") {{ +(product.price * (1 + (+product.section.margin || +product.establishment.margin))).toFixed(2) }}{{$vuetify.lang.t('$vuetify.hrn')}}
                v-row
                    v-col.py-0(cols="12" sm="4")
                        v-select(
                            :disabled="!!userBrandId"
                            v-model="product.brandId"
                            :label="$vuetify.lang.t('$vuetify.pages.Products.table.brand') + ' *'"
                            :error-messages="getValidateMessage('Product', 'product.brandId', 'brand')"
                            :items="brands"
                            item-value="id"
                            item-text="name"
                            outlined
                            clearable
                        )
                            template(v-slot:selection="{ item }")
                                span {{ item.id | brand }}
                            template(v-slot:item="{ item }")
                                span {{ item.id | brand }}
                    v-col.py-0(cols="12" sm="4")
                        v-select(
                            :disabled="!!userEstablishmentId || !product.brandId"
                            v-model="product.establishmentId"
                            :label="$vuetify.lang.t('$vuetify.pages.Products.table.establishment') + ' *'"
                            :error-messages="getValidateMessage('Product', 'product.establishmentId', 'establishment')"
                            :items="establishments.filter(i => +i.brandId === product.brandId)"
                            item-value="id"
                            item-text="name"
                            outlined
                        )
                            template(v-slot:selection="{ item }")
                                span {{ item.id | establishment }}
                            template(v-slot:item="{ item }")
                                span {{ item.id | establishment }}
                    v-col.py-0(cols="12" sm="4")
                        v-select(
                            outlined
                            v-model="product.establishmentAddressId"
                            item-value="id"
                            :label="$vuetify.lang.t('$vuetify.pages.Users.table.establishmentAddress') + ' *'"
                            :error-messages="getValidateMessage('Product', 'product.establishmentAddressId', 'establishmentAddressId')"
                            :items="establishmentAddresses"
                            :disabled="!product.brandId || !product.establishmentId || establishmentAddresses.length === 0"
                        )
                            template(v-slot:selection="{ item }")
                                span {{item.place | place}}
                            template(v-slot:item="{ item }")
                                span {{item.place | place}}
                v-row
                    v-col.py-0(cols="12" sm="4")
                        v-select(
                          :items="sections.filter(s => s.brandId === product.brandId && s.establishmentId === product.establishmentId && (s.establishmentAddressId === product.establishmentAddressId || !s.establishmentAddressId))"
                          :label="$vuetify.lang.t('$vuetify.pages.Products.table.section') + ' *'"
                          :error-messages="getValidateMessage('Product', 'product.sectionId', 'section')"
                          item-value="id"
                          :item-text="localizeField('name', $vuetify.lang.current)"
                          v-model="product.sectionId"
                          color="secondary"
                          hide-details
                          outlined
                        )
                    v-col.py-0(cols="12" sm="4")
                        v-select(
                            :items="statuses"
                            :disabled="!isEdit"
                            :label="$vuetify.lang.t('$vuetify.pages.Products.table.status')"
                            item-value="key"
                            item-text="text"
                            v-model="product.status"
                            color="secondary"
                            hide-details
                            outlined
                        )
                    v-col.py-0(cols="12" sm="4")
                        v-text-field(
                            v-model="product[localizeField('shortDescription', editingLocale)]"
                            :label="$vuetify.lang.t('$vuetify.pages.Products.table.shortDescription')"
                            outlined
                            counter="255"
                        )
                v-row
                    v-col.py-0(cols="12" sm="12")
                        v-textarea(
                            v-model="product[localizeField('detailedDescription', editingLocale)]"
                            :label="$vuetify.lang.t('$vuetify.pages.Products.table.detailedDescription')"
                            outlined
                            auto-grow
                            counter="1000"
                        )
                v-image-uploader(
                    :width="338"
                    :height="367"
                    :initial-image="product.imgUrl"
                    :input-label="$vuetify.lang.t('$vuetify.pages.Delivery.photo')"
                    :error-message="getValidateMessage('Product', 'product.image', 'image')"
                    class="pt-2"
                    @choose-image="onChooseImage"
                )
                .actions.d-flex.justify-end
                    v-btn(
                        :loading="requestProcessing"
                        v-if="canSaveChanges"
                        :disabled="!isCustomValid"
                        @click="saveProduct"
                        dark
                    ) {{ $vuetify.lang.t(`$vuetify.buttons.${isEdit ? 'save' : 'create'}`) }}
        v-btn.mx-2.arrow-back(to="/products" fab small dark)
            v-icon(dark) arrow_back
</template>

<style scoped lang="sass">
.redText
  color: #9b001d
</style>

<script>
import request from '@/utils/request';
import { getFormData } from '@/utils/helpers';
import { mapState, mapGetters, mapActions } from 'vuex';
import validate from '@/mixins/validate';
import { validationMixin } from 'vuelidate';
import { maxLength, required, decimal, requiredIf } from 'vuelidate/lib/validators';
import {localizeField, multilingualFieldKeys, multilingualFields} from "@/helpers/locales";

const vImageUploader = () => import('@/components/image-uploader');

const ProductStatuses = {
    REVIEW: 'review',
    DRAFT: 'draft',
    NOT_AVAILABLE: 'not-available',
    PUBLISH: 'publish'
};

export default {
    name: 'ProductEditor',
    components: {
        vImageUploader
    },
    mixins: [validate, validationMixin],
    data: () => ({
        product: {
            ...multilingualFields(['name', 'shortDescription', 'detailedDescription']),
            weight: null,
            price: null,
            status: ProductStatuses.DRAFT,
            brandId: null,
            image: null,
            isByWeight: false,
            imgUrl: null,
            sectionId: null,
            establishmentId: null,
            quantityType: 'g',
            establishmentAddressId: null
        },

        isLoading: false,
        requestProcessing: false,
        editingLocale: 'uk',

        establishmentAddresses: [],

        canSaveChanges: false,
    }),
    validations: {
        product: {
            weight: {
                required,
                decimal,
                maxLength: maxLength(8)
            },
            price: {
                required,
                decimal,
                maxLength: maxLength(8)
            },
            status: { required },
            establishmentId: { required },
            establishmentAddressId: { required },
            brandId: { required },
            sectionId: { required },
            image: {
                required: requiredIf(function () {
                    return !this.isEdit;
                }),
            }
        }
    },
    setup() {
        return {
            localizeField
        };
    },
    computed: {
        ...mapGetters(['userBrandId', 'userEstablishmentId', 'userEstablishmentAddressId']),
        ...mapState({
            brands: state => state.data.brands,
            establishments: state => state.data.establishments,
            sections: state => state.deliverySections.allItems
        }),
        isEdit() {
            return !!this.$route.params.id;
        },
        modelAsString() {
            return JSON.stringify(this.product);
        },
        statuses() {
            return Object.values(ProductStatuses).map(status => ({
                key: status,
                text: this.$vuetify.lang.t(`$vuetify.pages.Products.statuses.${status}`)
            }));
        },
        isCustomValid() {
            return multilingualFieldKeys(['name']).some((field) => !!this.product[field]) && multilingualFieldKeys(['shortDescription']).every((field) => (this.product[field] || '').length <= 255) && multilingualFieldKeys(['detailedDescription']).every((field) => (this.product[field] || '').length <= 1000);
        },
        quantityTypeOptions() {
            return ['g', 'pc', 'ml', 'kg'].map(i => ({id: i, name: this.$vuetify.lang.t('$vuetify.pages.Products.quantityTypes.' + i)}));
        }
    },
    watch: {
        modelAsString() {
            this.canSaveChanges = true;
        },
        async 'product.establishmentId'(currentValue, prevValue) {
          if (currentValue) {
              if (prevValue && currentValue !== prevValue) {
                this.product.establishmentAddressId = null;
              }

              await request({ method: 'get', url: `/delivery/establishment/${currentValue}/addresses` })
                  .then(res => {
                    if (res.data) {
                      this.establishmentAddresses = this.userEstablishmentAddressId ? [res.data.find(i => i.id === this.userEstablishmentAddressId)] : res.data;

                      // if (this.userEstablishmentAddressId || (!this.product.establishmentAddressId && this.establishmentAddresses.length === 1)) {
                      //     this.product.establishmentAddressId = this.establishmentAddresses[0].id;
                      // }
                    }
                  }).catch(error => {
                    if(error.response && error.response.data && error.response.data.error) {
                      this.$store.dispatch('alert/addMessage', {
                        message: error.response.data.error
                      }, { root: true });
                    }
                  });
          } else {
            this.establishmentAddresses = [];
            this.product.establishmentAddressId = null;
          }
        }
    },
    async created() {
        await this.getBrands();
        await this.getEstablishments();
        await this.getSections();

        if (this.userBrandId) {
            this.product.brandId = this.userBrandId;
        }

        if (this.userEstablishmentId) {
            this.product.establishmentId = this.userEstablishmentId;
        }

        if (this.isEdit) {
            this.isLoading = true;
            this.loadProduct();
        }
    },
    methods: {
        ...mapActions({
            getBrands: 'data/getBrands',
            getEstablishments: 'data/getEstablishments',
            getSections: 'deliverySections/getAllItems'
        }),
        onChooseImage(file) {
            this.canSaveChanges = true;
            this.product.image = file;
        },
        loadProduct() {
            const { id } = this.$route.params;
            const url = `/product/${id}`;

            return request({ method: 'get', url })
                .then(this.handleSuccess)
                .catch(this.handlerError)
                .finally(() => {
                    this.requestProcessing = false;
                });
        },
        handleSuccess(res) {
            const keys = Object.keys(this.product);
            this.product = { ...this.product, ...Object.select(res.data, keys),
                brandId: +res.data.brandId,
                sectionId: +res.data.sectionId,
                establishmentId: +res.data.establishmentId,
                establishmentAddressId: +res.data.establishmentAddressId,
                establishment: res.data.establishment,
                section: res.data.section
            };

            this.$nextTick(() => {
                this.canSaveChanges = false;
                this.isLoading = false;
            });
        },
        handlerError(error) {
            const message = error.response && error.response.data && error.response.data.error;

            if (message) {
                this.$store.dispatch('alert/addMessage', {
                    message: `products.${message}`,
                    time: 2000
                });
            }

            if (error.response && error.response.status === 404) {
                this.$router.push('/establishments');
            }
        },
        async saveProduct() {
            if (this.$v.product.$invalid) {
                this.$v.$touch();
                this.$nextTick(() => this.focusFirstErrorInput());
                return;
            }

          if (this.product.image && this.product.image.size > 5*1000*1000) {
            return this.$store.dispatch('alert/addMessage', {
              message: `imageIsTooLarge`,
              time: 2000
            });
          }

            if (this.requestProcessing) return;

            this.requestProcessing = true;

            const { id } = this.$route.params;
            const method = id ? 'put' : 'post';
            const url = id ? `/delivery/product/${id}` : '/delivery/product';

            const ignoredKeys = ['createdAt', 'updatedAt', 'imgUrl'];
            const preparedData = Object.reject(this.product, ignoredKeys);
            const data = getFormData(preparedData);

            return request({ method, url, data })
                .then(res => {
                    this.handleSuccess(res);
                    this.$store.dispatch('alert/addMessage', {
                        type: 'success',
                        message: 'success'
                    });

                    if (res.status === 201) {
                        this.$router.push(`/products/editor/${res.data.id}`);
                    }

                    this.requestProcessing = false;
                    this.$v.establishment.$reset();
                })
                .catch(this.handlerError)
                .finally(() => {
                    this.requestProcessing = false;
                });
        }
    }
};
</script>

<style lang="scss" scoped>
.editor-title {
    display: flex;
    justify-content: space-between;
}
</style>
